import get from "lodash-es/get";
import isArray from "lodash-es/isArray";
import isObject from "lodash-es/isObject";
import keys from "lodash-es/keys";
import merge from "lodash-es/merge";
import * as qs from "qs";
import snakeCaseKeys from "snakecase-keys";
import camelCaseKeys from "camelcase-keys";
import isPlainObject from "lodash-es/isPlainObject";
import { useSessionStore } from "~/stores/session";
import { Error_Codes } from "~/types/errors";
import { unRefObjectKeys } from "~/utils";

export interface ActualPaginationData {
  total: number;
  per_page: number;
  current_page: number;
  total_pages: number;
  first: string;
  current: string;
  last: string;
  prev: string;
  next: string;
}

export interface PaginationMeta {
  total: number;
  perPage: number;
  currentPage: number;
  totalPages: number;
}

export interface PaginationParams {
  limit: number;
  page: number;
}

export type BodyTransformType = "camel" | "snake" | "normal";

type RequestOptions = Parameters<typeof $fetch.raw<ResponseType>>[1] & {
  url?: string;
  transformRequestBodyCaseTo?: BodyTransformType;
  transformResponseBodyCaseTo?: BodyTransformType;
};

const bodyTransformMethods: Record<
  BodyTransformType,
  (body: Record<string, unknown> | Record<string, unknown>[]) => unknown
> = {
  snake: (data) => {
    return snakeCaseKeys(data, { deep: true });
  },
  camel: (data) => {
    return camelCaseKeys(data, { deep: true });
  },
  normal: (data) => {
    return data;
  },
};

type RequestOptionsWithURL = Omit<RequestOptions, "url"> & { url: string };
const useRequestOptions = function useRequestOptions(
  passedOptions:
    | RequestOptionsWithURL
    | {
        [key in keyof RequestOptionsWithURL]:
          | RequestOptionsWithURL[key]
          | Ref<RequestOptionsWithURL[key]>;
      },
): RequestOptions {
  const runtimeConfig = useRuntimeConfig();
  const sessionStore = useSessionStore();
  const { user } = storeToRefs(sessionStore);
  const { setToken } = useAuth();
  return merge<Partial<RequestOptions>, RequestOptions, RequestOptions>(
    { method: "GET" },
    unRefObjectKeys<RequestOptions>(passedOptions ?? {}) ?? {},
    {
      baseURL: runtimeConfig.public.apiUrl,
      retry: 3,
      onRequest({ options }) {
        const addHeader: Record<string, string> = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };

        if (user.value.accessToken) {
          addHeader.Authorization = `Bearer ${user.value.accessToken}`;
        }

        const addedHeaderList: Record<string, string>[] = [addHeader];

        const headers: HeadersInit = options.headers || {};
        for (const addedHeader of addedHeaderList) {
          for (const key of keys(addedHeader)) {
            const value = addedHeader[key];

            if (isArray(headers)) {
              headers.push([key, value]);
            } else if (headers instanceof Headers) {
              headers.set(key, value);
            } else if (isObject(headers)) {
              headers[key] = value;
            }
          }
        }

        options.headers = headers;
      },
      async onResponse({ response, options }) {
        const transformResponseCase: BodyTransformType =
          get(options, "transformResponseBodyCaseTo", "camel") || "camel";

        if (isPlainObject(response._data) || isArray(response._data)) {
          response._data = bodyTransformMethods[transformResponseCase](
            response._data,
          );
        }

        if (response.headers.has("NEW_AUTH_TOKEN")) {
          const token = response.headers.get("NEW_AUTH_TOKEN");

          if (token) {
            setToken(token);
          }
        }
      },
      async onResponseError(error) {
        const toast = useToast();
        const { logout } = useAuth();
        const status = parseInt(String(get(error, "response.status", "0")), 10);
        console.log("onResponseError", error, status);

        // customized retry logic based on possible server headers
        // if (
        //   (!status || (status >= 500 && status < 600)) &&
        //   Number(this.retry) < 3
        // ) {
        //   const retryAfter = error.response.headers.get("retry-after");
        //   const retryDelay = retryAfter ? parseInt(retryAfter, 10) * 1000 : 500;
        //   await new Promise((resolve) => setTimeout(resolve, retryDelay));
        //   await $fetch.raw(
        //     toValue(passedOptions.url),
        //     useRequestOptions.call(this, passedOptions),
        //   );
        // }

        if (status === Error_Codes.AUTHENTICATION_ERROR) {
          if (import.meta.client) {
            try {
              toast.error((error.response._data as APIRequestError).message, {
                id: "auth-error",
                timeout: 1000,
              });
            } catch (error) {
              if (inDevelopment.value) {
                console.log("Error caught in on resposnse error ====> ");
                console.dir(error);
              }
            }
          }
          await isModuleReady(sessionStore);
          await logout(false);
        }
      },
    },
  ) as RequestOptions;
};

export const useRequest = <ResponseType>(
  url: Parameters<typeof $fetch.raw<ResponseType>>[0],
  opts?: RequestOptions,
): Promise<ReturnType<typeof $fetch.raw<ResponseType>>> => {
  let options = opts;
  if (!isObject(options)) {
    options = {};
  }

  options = {
    ...useRequestOptions({
      url: url as string,
      ...options,
    }),
  };

  let transformRequestBodyCase = get(
    options,
    "transformRequestBodyCaseTo",
    "snake",
  ) as BodyTransformType | undefined;

  if (
    !transformRequestBodyCase ||
    !["snake", "camel", "normal"].includes(transformRequestBodyCase)
  ) {
    transformRequestBodyCase = "snake";
  }

  let params: Record<string, unknown> = {};
  if (options.body && isObject(options.body)) {
    if (isPlainObject(options.body) || isArray(options.body)) {
      options.body = bodyTransformMethods[transformRequestBodyCase](
        options.body as unknown as
          | Record<string, unknown>
          | Record<string, unknown>[],
      ) as unknown as Record<string, unknown> | Record<string, unknown>[];
    }
  }

  if (options.query) {
    params = merge(params, options.query);
  }

  if (options.params) {
    params = merge(params, options.params);
  }

  delete options.params;
  delete options.query;

  const query = qs.stringify(params);

  if (query) {
    const params = new URLSearchParams(query);
    options.params = Object.fromEntries(params);
  }

  return $fetch.raw(url, options) as unknown as Promise<
    ReturnType<typeof $fetch.raw<ResponseType>>
  >;
};

export const useFetchRequest = async <ResponseType, ErrorType>(
  ...args: Parameters<typeof useFetch<ResponseType, ErrorType>>
) => {
  const [url, opts] = args;

  let options = opts!;
  if (!isObject(options)) {
    options = {};
  }

  options = {
    ...useRequestOptions({
      url: url as string,
      ...options,
    } as {
      [key in keyof RequestOptionsWithURL]:
        | RequestOptionsWithURL[key]
        | Ref<RequestOptionsWithURL[key]>;
    }),
    method: options.method,
    lazy: true,
  };

  let params = {};
  if (options && options.query) {
    params = merge(params, options.query);
  }

  if (options && options.params) {
    params = merge(params, options.params);
  }

  if (options) {
    delete options.params;
    delete options.query;
  }

  const query = qs.stringify(params);
  if (query) {
    const params = new URLSearchParams(query);
    options.params = Object.fromEntries(params);
  }

  return await useFetch<ResponseType, ErrorType>(url, options);
};

export function useCancelRequests(
  aborter: AbortController,
  cancelOnLogout = true,
) {
  const sessionStore = useSessionStore();
  watch(
    () => sessionStore.isLoggedIn,
    (value) => {
      if (value) return;
      if (cancelOnLogout) aborter.abort("user-is-logged-out");
    },
    { immediate: true, flush: "sync" },
  );
}
